import {
    EMAIL_COMPOSED_STATUSES,
    isErpDocumentListOutput,
    isErpDocumentOutput,
    parsePriceResult,
    parseToolOutput,
} from './toolDisplay.tsx';

export const humanizeKey = (key: string) => key.replace(/_/g, ' ');

// technical ids that clutter the result rows — the id is still visible in the call's parameters panel
const HIDDEN_RESULT_KEYS = new Set(['message_id', 'in_reply_to_message_id']);

export const buildToolResultRows = (output, prefix = ''): [string, string][] => {
    if (output === null || output === undefined || output === '') return [];
    if (typeof output !== 'object') {
        const value = String(output);
        return [[prefix || 'result', value.length > 120 ? `${value.slice(0, 120)}…` : value]];
    }
    if (Array.isArray(output)) {
        if (output.every((item) => typeof item !== 'object')) {
            return [[prefix || 'result', output.join(', ')]];
        }
        return output
            .slice(0, 3)
            .flatMap((item, idx) =>
                buildToolResultRows(item, output.length > 1 ? `${prefix} ${idx + 1}`.trim() : prefix)
            );
    }
    return Object.entries(output)
        .filter(([key]) => !HIDDEN_RESULT_KEYS.has(key))
        .flatMap(([key, value]) =>
            buildToolResultRows(value, prefix ? `${prefix} · ${humanizeKey(key)}` : humanizeKey(key))
        );
};

export const isTableOutput = (output) =>
    Boolean(
        output &&
            typeof output === 'object' &&
            Array.isArray(output.columns) &&
            Array.isArray(output.rows) &&
            output.rows.every((row) => Array.isArray(row))
    );

export const isDocumentListOutput = (output) =>
    Boolean(output && typeof output === 'object' && Array.isArray(output.documents));

export const isEmailListOutput = (output) =>
    Boolean(output && typeof output === 'object' && Array.isArray(output.emails));

// the article search reports the total match count alongside the requested page of candidates
export const isArticleListOutput = (output) =>
    Boolean(
        output && typeof output === 'object' && Array.isArray(output.results) && typeof output.total_count === 'number'
    );

// the article lookup echoes every number it was asked for, so a miss still has a row: an empty "matches" list
const isEmptyLookupOutput = (output) =>
    Boolean(
        output &&
            typeof output === 'object' &&
            Array.isArray(output.results) &&
            output.results.length > 0 &&
            output.results.every((result) => Array.isArray(result?.matches) && result.matches.length === 0)
    );

export const isNoDataResult = (toolCall): boolean => {
    const toolName = toolCall.tool_name || '';
    const output = parseToolOutput(toolCall.tool_output);
    const failed = Boolean(output && typeof output === 'object' && !Array.isArray(output) && output.error);
    if (failed || toolCall.status === 'cancelled') return false;
    if (isEmptyLookupOutput(output)) return true;
    if (isTableOutput(output)) return output.rows.length === 0;
    if (isDocumentListOutput(output)) return output.documents.length === 0;
    if (isArticleListOutput(output)) return output.results.length === 0;
    const erpOutput =
        isErpDocumentOutput(output) ||
        isErpDocumentListOutput(output) ||
        Boolean(output && typeof output === 'object' && Array.isArray(output.created));
    const emailComposed =
        /SendEmail/i.test(toolName) &&
        output &&
        typeof output === 'object' &&
        EMAIL_COMPOSED_STATUSES.has(output.status);
    if (erpOutput || isEmailListOutput(output) || emailComposed) return false;
    if (/Price/i.test(toolName) && parsePriceResult(output)) return false;
    const webLink = output && typeof output === 'object' ? output.web_link : null;
    return (
        buildToolResultRows(webLink ? { ...output, web_link: undefined } : output).filter(
            ([, value]) => String(value).trim() !== ''
        ).length === 0
    );
};
